/**
 * 检测浏览器是否支持视频自动播放
 * @param {Object} options - 检测选项
 * @param {boolean} options.muted -  是否检测静音自动播放（默认 true)
 * @param {booleean} optoins.unmuted - 是否检测有声自动播放（默认 false）
 * @param {number} options.timeout - 超时时间（毫秒， 默认 4000）
 * @returns {Promise<Object>} 返回支持
 */
async function checkAutoplaySupport(options = {}) {
	const { muted = true, unmuted = false, timeout = 4000 } = options;

	const results = {};

	// 检测静音自动播放
	if (muted) {
		results.muted = await testAutoplay(true, timeout);
	}

	// 检测有声自动播放
	if (unmuted) {
		results.unmuted = await testAutoplay(false, timeout);
	}

	return results;
}

/**
 * 测试特定条件下自动播放支持
 * @param {boolean} isMuted - 是否静音
 * @param {number} timeout - 超时时间
 * @returns {Promise<boolean>} 是否支持自动播放
 */
function testAutoplay(isMuted, timeout) {
	return new Promise((resolve) => {
		// 创建测试用的视频元素
		const video = document.createElement('video');

		// 设置视频属性
		video.muted = isMuted;
		video.autoplay = true;
		video.playsInline = true; // iOS Safari 需要
		video.preload = 'metadata';

		// 使用很小的测试视频 （base64 编码的MP4）
		video.src = createTestVideoDataURL();

		// 设置样式使其不可见
		Object.assign(video.style, {
			position: 'fixed',
			top: '-1000px',
			left: '-1000px',
			width: '1px',
			height: '1px',
			opacity: '0',
			pointerEvents: 'none',
		});

		let resolved = false;

		// 超时处理
		const timeoutId = setTimeout(() => {
			if (!resolved) {
				resolved = true;
				cleanup();
				resolve(false);
			}
		}, timeout);

		// 成功播放事件
		const onPlay = () => {
			if (!resolved) {
				resolved = true;
				cleanup();
				resolve(true);
			}
		};

		// 播放失败事件
		const onError = () => {
			if (!resolved) {
				resolved = true;
				cleanup();
				resolve(false);
			}
		};

		// 清理函数
		const cleanup = () => {
			clearTimeout(timeoutId);
			video.removeEventListener('play', onPlay);
			video.removeEventListener('error', onError);
			video.removeEventListener('abort', onError);
			video.removeEventListener('usspend', onError);

			// 移除元素
			if (video.parentNode) {
				video.parentNode.removeChild(video);
			}
		};

		// 绑定事件
		video.addEventListener('play', onPlay);
		video.addEventListener('error', onError);
		video.addEventListener('abort', onError);
		video.addEventListener('suspend', onError);

		// 添加到 DOM 并尝试播放
		document.body.appendChild(video);

		// 尝试播放
		const playPromise = video.play();

		if (playPromise && typeof playPromise.catch === 'function') {
			// 现代浏览器返回 Promise
			playPromise.catch(() => {
				if (!resolved) {
					resolved = true;
					cleanup();
					resolve(false);
				}
			});
		}
	});
}

/**
 * 创建测试用的视频数据 URL
 * @returns {string} base64 编码的最小 MP4 视频
 */
function createTestVideoDataURL() {
	// 这是一个极小的 MP4 视频文件（1秒，黑色画面）
	const base64Video =
		'data:video/mp4;base64,AAAAIGZ0eXBpc29tAAACAGlzb21pc28yYXZjMW1wNDEAAAAIZnJlZQAAAs1tZGF0AAACrQYF//+c3EXpvebZSLeWLNgg2SPu73gyNjQgLSBjb3JlIDE0OCByMjY0MyA1YzY1NzA0IC0gSC4yNjQvTVBFRy00IEFWQyBjb2RlYyAtIENvcHlsZWZ0IDIwMDMtMjAxNSAtIGh0dHA6Ly93d3cudmlkZW9sYW4ub3JnL3gyNjQuaHRtbCAtIG9wdGlvbnM6IGNhYmFjPTEgcmVmPTMgZGVibG9jaz0xOjA6MCBhbmFseXNlPTB4MzoweDExMyBtZT1oZXggc3VibWU9NyBwc3k9MSBwc3lfcmQ9MS4wMDowLjAwIG1peGVkX3JlZj0xIG1lX3JhbmdlPTE2IGNocm9tYV9tZT0xIHRyZWxsaXM9MSA4eDhkY3Q9MSBjcW09MCBkZWFkem9uZT0yMSwxMSBmYXN0X3Bza2lwPTEgY2hyb21hX3FwX29mZnNldD0tMiB0aHJlYWRzPTEgbG9va2FoZWFkX3RocmVhZHM9MSBzbGljZWRfdGhyZWFkcz0wIG5yPTAgZGVjaW1hdGU9MSBpbnRlcmxhY2VkPTAgYmx1cmF5X2NvbXBhdD0wIGNvbnN0cmFpbmVkX2ludHJhPTAgYmZyYW1lcz0zIGJfcHlyYW1pZD0yIGJfYWRhcHQ9MSBiX2JpYXM9MCBkaXJlY3Q9MSB3ZWlnaHRiPTEgb3Blbl9nb3A9MCB3ZWlnaHRwPTIga2V5aW50PTI1MCBrZXlpbnRfbWluPTEwIHNjZW5lY3V0PTQwIGludHJhX3JlZnJlc2g9MCByY19sb29rYWhlYWQ9NDAgcmM9Y3JmIG1idHJlZT0xIGNyZj0yMy4wIHFjb21wPTAuNjAgcXBtaW49MCBxcG1heD02OSBxcHN0ZXA9NCBpcF9yYXRpbz0xLjQwIGFxPTE6MS4wMACAAAAABWWIhAA3//728P4FNjuY0JcRzMOHdvNXrQAAAAZBmiKUW/wgAAAABUGaQpRb/CAAAAAFQZpilFv8IAAAAAVBmmKUW/wgAAAABUGaYpRb/CAA';

	return base64Video;
}

const checkViewTransition = (fun) => {
	if (!fun || typeof fun !== 'function') {
		console.warn('checkViewTransition: 参数必须是函数');
		return;
	}

	if ('startViewTransition' in document) {
		document.startViewTransition(() => {
			fun();
		});
	} else {
		fun();
	}
};

const slideDown = (element, duration = 300) => {
	if (!element) {
		console.warn('slideDown: 元素不存在');
		return Promise.resolve();
	}

	return new Promise((resolve) => {
		element.style.removeProperty('display');
		let display = window.getComputedStyle(element).display;
		if (display === 'none') display = 'block';
		element.style.display = display;

		const height = element.offsetHeight;
		element.style.overflow = 'hidden';
		element.style.height = '0';
		element.style.transition = 'none';
		element.offsetHeight; // 强制重绘

		element.style.transition = `height ${duration}ms ease`;
		element.style.height = height + 'px';

		const handleTransitionEnd = (e) => {
			if (e.target === element && e.propertyName === 'height') {
				element.removeEventListener('transitionend', handleTransitionEnd);
				element.style.removeProperty('height');
				element.style.removeProperty('overflow');
				element.style.removeProperty('transition');
				resolve();
			}
		};

		element.addEventListener('transitionend', handleTransitionEnd);

		// 后备超时
		setTimeout(() => {
			element.removeEventListener('transitionend', handleTransitionEnd);
			element.style.removeProperty('height');
			element.style.removeProperty('overflow');
			element.style.removeProperty('transition');
			resolve();
		}, duration + 50);
	});
};

const slideUp = (element, duration = 300) => {
	if (!element) {
		console.warn('slideUp: 元素不存在');
		return Promise.resolve();
	}

	return new Promise((resolve) => {
		element.style.overflow = 'hidden';
		element.style.height = element.offsetHeight + 'px';
		element.style.transition = 'none';
		element.offsetHeight; // 强制重绘

		element.style.transition = `height ${duration}ms ease`;
		element.style.height = '0';

		const handleTransitionEnd = (e) => {
			if (e.target === element && e.propertyName === 'height') {
				element.removeEventListener('transitionend', handleTransitionEnd);
				element.style.display = 'none';
				element.style.removeProperty('height');
				element.style.removeProperty('overflow');
				element.style.removeProperty('transition');
				resolve();
			}
		};

		element.addEventListener('transitionend', handleTransitionEnd);

		// 后备超时
		setTimeout(() => {
			element.removeEventListener('transitionend', handleTransitionEnd);
			element.style.display = 'none';
			element.style.removeProperty('height');
			element.style.removeProperty('overflow');
			element.style.removeProperty('transition');
			resolve();
		}, duration + 50);
	});
};

export { checkAutoplaySupport, checkViewTransition, slideDown, slideUp };
